import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import "./JobOpportunityInterviewSessions.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const TYPE_OPTIONS = ["TECHNICAL", "NONTECHNICAL"];
const MODE_OPTIONS = ["VIDEO", "PHONE", "INPERSON", "ASSIGNMENT"];
const STATUS_OPTIONS = ["SCHEDULED", "CANCELLED", "COMPLETED"];
const FEEDBACK_OPTIONS = ["NONE", "PENDING", "PASSED", "FAILED"];

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const formatDateTime = (value) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const numericValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const clampOrderNumber = (value) => Math.max(1, Math.min(50, Math.round(numericValue(value) || 1)));

const interviewersText = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  return firstText(value);
};

const interviewFeedbackLabel = (value) => {
  const normalized = String(value || "NONE").toUpperCase();
  if (normalized === "NONE") return "Feedback: Not started";
  return `Feedback: ${titleCase(normalized)}`;
};

const sortSessions = (sessions) =>
  [...sessions].sort((left, right) => {
    const leftOrder = Number(left.orderNumber ?? left.orderMumber ?? Number.MAX_SAFE_INTEGER);
    const rightOrder = Number(right.orderNumber ?? right.orderMumber ?? Number.MAX_SAFE_INTEGER);
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return String(left.title || "").localeCompare(String(right.title || ""));
  });

const nextTemplateOrderNumber = (sessions, session) => {
  if (session?.orderNumber || session?.orderMumber) return clampOrderNumber(session.orderNumber ?? session.orderMumber);
  const highest = sessions.reduce((max, item) => {
    const value = Number(item.orderNumber ?? item.orderMumber);
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0);
  return clampOrderNumber(highest + 1);
};

const optionLabel = (value) => (String(value).toUpperCase() === "NONE" ? "Not started" : titleCase(value));

const sessionInitialValues = (session, sessions = []) => ({
  title: session?.title || "",
  type: session?.type || "TECHNICAL",
  interviewMode: session?.interviewMode || "VIDEO",
  orderNumber: nextTemplateOrderNumber(sessions, session),
  durationMinutes: session?.durationMinutes ?? session?.duration ?? 30,
  description: session?.description || "",
  location: session?.location || "",
  status: session?.status || "SCHEDULED",
  interviewFeedbackStatus: session?.interviewFeedbackStatus || "NONE",
  interviewFeedbackInternal: session?.interviewFeedbackInternal || "",
  interviewFeedbackToCandidate: session?.interviewFeedbackToCandidate || "",
  interviewers: interviewersText(session?.interviewers),
  notesToCandidate: session?.notesToCandidate || "",
});

const buildPayload = (values) => {
  const duration = numericValue(values.durationMinutes);
  return {
    title: String(values.title || "").trim(),
    type: values.type || "TECHNICAL",
    interviewMode: values.interviewMode || "VIDEO",
    interviewDateTime: null,
    durationMinutes: duration ? Math.max(1, Math.round(duration)) : null,
    description: String(values.description || "").trim() || null,
    meetingLink: null,
    location: String(values.location || "").trim() || null,
    status: values.status || "SCHEDULED",
    interviewFeedbackStatus: values.interviewFeedbackStatus || "NONE",
    interviewFeedbackInternal: String(values.interviewFeedbackInternal || "").trim() || null,
    interviewFeedbackToCandidate: String(values.interviewFeedbackToCandidate || "").trim() || null,
    interviewers: String(values.interviewers || "").split(",").map((item) => item.trim()).filter(Boolean),
    notesToCandidate: String(values.notesToCandidate || "").trim() || null,
    orderNumber: clampOrderNumber(values.orderNumber),
  };
};

function OptionChips({ value = "", options = [], onChange }) {
  return (
    <div className="job-interview-template-option-wrap">
      {options.map((option) => {
        const selected = String(value).toUpperCase() === option;
        return (
          <button
            key={option}
            type="button"
            className={`job-interview-template-option-chip${selected ? " is-selected" : ""}`}
            onClick={() => onChange(option)}
          >
            {optionLabel(option)}
          </button>
        );
      })}
    </div>
  );
}

export default function JobOpportunityInterviewSessions() {
  const { jobOpportunityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [form] = Form.useForm();

  const [job, setJob] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState({});

  const sortedSessions = useMemo(() => sortSessions(sessions), [sessions]);

  const loadData = useCallback(async () => {
    if (!jobOpportunityId || !actorId) return;
    setLoading(true);
    setError("");
    try {
      const [nextJob, nextSessions] = await Promise.all([
        olreadiaService.jobOpportunities.get(jobOpportunityId, actorId),
        olreadiaService.jobOpportunities.listInterviewSessions(jobOpportunityId, actorId),
      ]);
      setJob(nextJob || null);
      setSessions(nextSessions || []);
    } catch (nextError) {
      setError(nextError?.message || "Could not load interview session templates.");
    } finally {
      setLoading(false);
    }
  }, [actorId, jobOpportunityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditor({ session: null });
    form.setFieldsValue(sessionInitialValues(null, sessions));
  };

  const openEdit = (session) => {
    setEditor({ session });
    form.setFieldsValue(sessionInitialValues(session, sessions));
  };

  const closeEditor = () => {
    setEditor(null);
    form.resetFields();
  };

  const submitSession = async () => {
    if (!jobOpportunityId || !editor) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = buildPayload(values);
      if (editor.session?.id) {
        const updated = await olreadiaService.jobOpportunities.updateInterviewSession(
          jobOpportunityId,
          editor.session.id,
          payload,
          actorId,
        );
        setSessions((current) => current.map((item) => (item.id === editor.session.id ? updated || { ...item, ...payload } : item)));
        message.success("Interview updated. Your changes have been saved.");
      } else {
        const created = await olreadiaService.jobOpportunities.createInterviewSessions(jobOpportunityId, [payload], actorId);
        setSessions((current) => sortSessions([...current, ...(created || [])]));
        message.success("Interview added. Your changes have been saved.");
      }
      closeEditor();
    } catch (nextError) {
      if (nextError?.errorFields) return;
      message.error(nextError?.message || "Unable to save interview session template.");
    } finally {
      setSaving(false);
    }
  };

  const reorderSession = async (session, direction) => {
    const currentIndex = sortedSessions.findIndex((item) => item.id === session.id);
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= sortedSessions.length || !session.id) return;
    const nextOrdered = [...sortedSessions];
    const [moved] = nextOrdered.splice(currentIndex, 1);
    nextOrdered.splice(targetIndex, 0, moved);
    const renumbered = nextOrdered.map((item, index) => ({ ...item, orderNumber: index + 1 }));
    setSessions(renumbered);
    setReordering(true);
    try {
      await Promise.all(
        renumbered.map((item) =>
          item.id
            ? olreadiaService.jobOpportunities.updateInterviewSession(
                jobOpportunityId,
                item.id,
                { orderNumber: item.orderNumber },
                actorId,
              )
            : Promise.resolve(),
        ),
      );
    } catch (nextError) {
      message.error(nextError?.message || "Unable to reorder interview session templates.");
      await loadData();
    } finally {
      setReordering(false);
    }
  };

  const toggleExpanded = (session, index) => {
    const key = session.id || `${session.title}-${index}`;
    setExpandedKeys((current) => ({ ...current, [key]: !current[key] }));
  };

  if (loading) return <div className="job-interview-template-page"><Skeleton active /></div>;

  if (error) {
    return (
      <div className="job-interview-template-page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobOpportunityDetails(jobOpportunityId))} className="job-interview-template-back">
          Back to job post
        </Button>
        <Alert type="error" showIcon message="Unable to load interview session templates" description={error} action={<Button onClick={loadData}>Retry</Button>} />
      </div>
    );
  }

  return (
    <div className="job-interview-template-page">
      <div className="job-interview-template-layout">
        <div className="job-interview-template-main">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobOpportunityDetails(jobOpportunityId))} className="job-interview-template-back">
            Back to job post
          </Button>

          <section className="job-interview-template-hero">
            <span className="job-interview-template-hero__icon"><CalendarOutlined /></span>
            <div>
              <Text className="job-interview-template-eyebrow">Interview Session Templates</Text>
              <Title level={2}>{job?.title || "Job post"}</Title>
              <Paragraph>Templates are copied into application interview sessions when candidates are moved to interviewing.</Paragraph>
            </div>
            <Button type="primary" icon={<PlusCircleOutlined />} onClick={openCreate}>
              Add Interview Session Template
            </Button>
          </section>

          <Card className="job-interview-template-card">
            {!sortedSessions.length ? (
              <Empty description="No interview session templates have been added yet.">
                <Button type="primary" icon={<PlusCircleOutlined />} onClick={openCreate}>
                  Add Interview Session Template
                </Button>
              </Empty>
            ) : (
              <List
                dataSource={sortedSessions}
                renderItem={(session, index) => {
                  const key = session.id || `${session.title}-${index}`;
                  const expanded = Boolean(expandedKeys[key]);
                  const duration = session.durationMinutes ?? session.duration;
                  const candidateLink = session.meetingLink || session.metingLink;
                  const orderNumber = session.orderNumber ?? session.orderMumber ?? index + 1;
                  return (
                    <List.Item className="job-interview-template-list-item">
                      <Card className="job-interview-template-session-card">
                        <div className="job-interview-template-session-card__top">
                          <span className="job-interview-template-session-card__order">{index + 1}</span>
                          <div className="job-interview-template-session-card__title">
                            <strong>{session.title || "Interview session template"}</strong>
                          </div>
                          <Tag color={String(session.status || "").toUpperCase() === "CANCELLED" ? "red" : "green"}>
                            {titleCase(session.status || "Scheduled")}
                          </Tag>
                          <Space size={6} className="job-interview-template-session-card__actions">
                            <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0 || reordering} onClick={() => reorderSession(session, -1)} />
                            <Button size="small" icon={<ArrowDownOutlined />} disabled={index === sortedSessions.length - 1 || reordering} onClick={() => reorderSession(session, 1)} />
                            <Button size="small" icon={<EditOutlined />} disabled={reordering} onClick={() => openEdit(session)}>Edit</Button>
                            <Button size="small" icon={<EyeOutlined />} onClick={() => toggleExpanded(session, index)}>
                              {expanded ? "Hide" : "View"}
                            </Button>
                          </Space>
                        </div>

                        <div className="job-interview-template-session-card__tags">
                          <Tag>Order {orderNumber}</Tag>
                          {session.type ? <Tag>{titleCase(session.type)}</Tag> : null}
                          {session.interviewMode ? <Tag>{titleCase(session.interviewMode)}</Tag> : null}
                          {duration ? <Tag>{duration} min</Tag> : null}
                          <Tag>{interviewFeedbackLabel(session.interviewFeedbackStatus)}</Tag>
                        </div>

                        {expanded ? (
                          <div className="job-interview-template-session-details">
                            <div><Text type="secondary">Location</Text><span>{session.location || "Location not set"}</span></div>
                            {candidateLink ? (
                              <div className="is-wide">
                                <Text type="secondary">Meeting Link</Text>
                                <Button type="link" href={candidateLink} target="_blank" rel="noreferrer">{candidateLink}</Button>
                              </div>
                            ) : null}
                            <div><Text type="secondary">Interviewers</Text><span>{interviewersText(session.interviewers) || "Not set"}</span></div>
                            {session.createdAt ? <div><Text type="secondary">Created</Text><span>{formatDateTime(session.createdAt)}</span></div> : null}
                            {session.updatedAt ? <div><Text type="secondary">Updated</Text><span>{formatDateTime(session.updatedAt)}</span></div> : null}
                            {session.description ? <Paragraph className="is-wide">{session.description}</Paragraph> : null}
                            {session.notesToCandidate ? <div className="is-wide is-note"><Text>Notes to candidate</Text><span>{session.notesToCandidate}</span></div> : null}
                            {session.interviewFeedbackToCandidate ? <div className="is-wide is-note"><Text>Feedback to candidate</Text><span>{session.interviewFeedbackToCandidate}</span></div> : null}
                            {session.interviewFeedbackInternal ? <div className="is-wide is-note is-internal"><Text>Internal feedback</Text><span>{session.interviewFeedbackInternal}</span></div> : null}
                          </div>
                        ) : null}
                      </Card>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </div>
        <SidePromotionSection />
      </div>

      <Drawer
        open={Boolean(editor)}
        title={editor?.session ? "Edit Interview Session Template" : "New Interview Session Template"}
        onClose={closeEditor}
        width="min(680px, 100vw)"
        className="job-interview-template-drawer"
        destroyOnClose
      >
        <div className="job-interview-template-drawer-hero">
          <span className="job-interview-template-drawer-hero__icon"><CalendarOutlined /></span>
          <div>
            <Title level={4}>{editor?.session ? "Edit Interview Session Template" : "New Interview Session Template"}</Title>
            <Text>This template belongs to the job post and can be copied to application interviews.</Text>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          className="job-interview-template-form"
          initialValues={sessionInitialValues(editor?.session, sessions)}
        >
          <div className="job-interview-template-form-card">
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Add a title for the interview session." }]}>
              <Input placeholder="Technical screen" />
            </Form.Item>

            <Form.Item name="type" label="Type" rules={[{ required: true }]}>
              <OptionChips options={TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item name="interviewMode" label="Mode" rules={[{ required: true }]}>
              <OptionChips options={MODE_OPTIONS} />
            </Form.Item>
            <Form.Item name="status" label="Status">
              <OptionChips options={STATUS_OPTIONS} />
            </Form.Item>
            <Form.Item name="interviewFeedbackStatus" label="Interview Feedback">
              <OptionChips options={FEEDBACK_OPTIONS} />
            </Form.Item>

            <div className="job-interview-template-schedule-row">
              <Form.Item name="orderNumber" label="Order Number" className="job-interview-template-order-field">
                <InputNumber min={1} max={50} placeholder="1" className="job-interview-template-form__number" />
              </Form.Item>
              <Form.Item name="durationMinutes" label="Minutes" className="job-interview-template-duration-field">
                <InputNumber min={1} max={600} addonAfter="min" placeholder="30" className="job-interview-template-form__number" />
              </Form.Item>
            </div>

            <Form.Item name="interviewers" label="Interviewers">
              <Input placeholder="Names, comma separated" />
            </Form.Item>

            <Form.Item label="Location" shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const currentLocation = getFieldValue("location");
                return (
                  <div className="job-interview-template-location-row">
                    <Form.Item name="location" noStyle>
                      <Input placeholder="Search and pick address" />
                    </Form.Item>
                    <Button
                      icon={<VideoCameraOutlined />}
                      className={currentLocation === "Remote" ? "is-active" : ""}
                      onClick={() => setFieldsValue({ location: "Remote" })}
                    >
                      Remote
                    </Button>
                  </div>
                );
              }}
            </Form.Item>

            <Form.Item name="description" label="Description">
              <TextArea rows={3} placeholder="What this session covers" />
            </Form.Item>
            <Form.Item name="notesToCandidate" label="Notes To Candidate">
              <TextArea rows={3} placeholder="Preparation notes visible to candidates" />
            </Form.Item>
            <Form.Item name="interviewFeedbackToCandidate" label="Feedback To Candidate">
              <TextArea rows={3} placeholder="Candidate-visible feedback" />
            </Form.Item>
            <Form.Item name="interviewFeedbackInternal" label="Internal Feedback">
              <TextArea rows={3} placeholder="Private hiring team feedback" />
            </Form.Item>

            <Button
              type="primary"
              loading={saving}
              className="job-interview-template-save-button"
              onClick={submitSession}
              block
            >
              {editor?.session ? "Save Interview" : "Create Interview"}
            </Button>
          </div>
        </Form>
      </Drawer>
    </div>
  );
}
